import Image from "next/image"
import { useState } from "react"
import { Card, FileInput, Stack, StackItem, Typography } from "@octopus-energy/coral/atoms"
import { FormikTextField } from "@octopus-energy/coral/formik"
import { useFormikContext } from "formik"
import { useTranslations } from "next-intl"
import { type InvoiceUploadFormValues } from "../../types/invoiceUploadFormValues"

const MAX_FILE_SIZE_MB = 1.8
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

export function InvoiceUploadCardFormFields() {
    const t = useTranslations("InvoiceUploadCardFormFields")
    const { setFieldValue } = useFormikContext<InvoiceUploadFormValues>()

    const [fileSizeError, setFileSizeError] = useState(false)

    const handleFileChange = async (files: FileList | null) => {
        if (files && files.length > 0) {
            const file = files[0]

            if (file.size > MAX_FILE_SIZE_BYTES) {
                setFileSizeError(true)
                await setFieldValue("file", null)

                return
            }

            setFileSizeError(false)
            await setFieldValue("file", file)
        }
    }

    const handleFileRemove = async () => {
        setFileSizeError(false)
        await setFieldValue("file", null)
    }

    return (
        <Stack
            flexDirection="column"
            gap="lg"
        >
            <Typography textStyle="h4">{t("title")}</Typography>
            <FileInput
                accept=".pdf"
                buttonLabel={t("fileInput.buttonLabel")}
                buttonPosition="center"
                dropzoneTitle={t("fileInput.dropzoneTitle")}
                dropzoneCaption={t("fileInput.dropzoneCaption")}
                error={fileSizeError}
                errorMessage={
                    fileSizeError
                        ? t("fileInput.fileTooLargeError", { maxSize: MAX_FILE_SIZE_MB })
                        : t("fileInput.errorMessage")
                }
                uploadingLabel={t("fileInput.uploadingLabel")}
                onChange={handleFileChange}
                onRemove={handleFileRemove}
            />
            <Card
                theme="secondary900"
                padding="small"
                borderless
            >
                <Stack>
                    <Image
                        width={178}
                        height={151}
                        src="/assets/images/illustrations/constantine-calculator-math.svg"
                        alt={t("analysisInfo.imageAlt")}
                        style={{ width: "80px", height: "auto" }}
                    />
                    <Typography textStyle="body2">
                        <b>{t("analysisInfo.title")}</b>
                        <br />
                        {t("analysisInfo.description")}
                    </Typography>
                </Stack>
            </Card>
            <Typography textStyle="h4">{t("personalInfo.title")}</Typography>
            <Stack>
                <StackItem flex="1">
                    <FormikTextField
                        name="givenName"
                        label={t("personalInfo.givenName")}
                    />
                </StackItem>
                <StackItem flex="1">
                    <FormikTextField
                        name="familyName"
                        label={t("personalInfo.familyName")}
                    />
                </StackItem>
            </Stack>
        </Stack>
    )
}

InvoiceUploadCardFormFields.messages = ["InvoiceUploadCardFormFields"] as const
